import type { QueryClient } from "@tanstack/react-query"
import {
  HeadContent,
  Link,
  Scripts,
  createRootRouteWithContext,
} from "@tanstack/react-router"
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools"
import { TanStackDevtools } from "@tanstack/react-devtools"
import { Satellite } from "lucide-react"
import { useEffect } from "react"

import { TooltipProvider } from "@/components/ui/tooltip"
import appCss from "../styles.css?url"

type RouterContext = {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: "Service Portal · Earth observation imagery demo",
      },
      {
        name: "description",
        content:
          "A synthetic portfolio demo of an earth-observation imagery search and request workflow.",
      },
      {
        name: "theme-color",
        content: "#123A5A",
      },
      {
        name: "color-scheme",
        content: "light",
      },
      {
        property: "og:title",
        content: "Service Portal · Earth observation imagery demo",
      },
      {
        property: "og:description",
        content:
          "Explore a realistic, production-shaped satellite imagery search and request workflow using synthetic data.",
      },
      {
        property: "og:type",
        content: "website",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "icon",
        type: "image/svg+xml",
        href: "/service-mark.svg",
      },
      {
        rel: "manifest",
        href: "/manifest.json",
      },
    ],
  }),
  notFoundComponent: () => (
    <main className="grid min-h-svh place-items-center bg-[var(--cloud)] px-6 py-16 text-center">
      <div className="max-w-md">
        <span className="mx-auto grid size-12 place-items-center rounded-[14px] bg-[var(--navy)] text-white">
          <Satellite className="size-5" />
        </span>
        <p className="mt-5 text-sm font-semibold text-[var(--service-blue)]">
          Signal not found
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-[-0.025em] text-[var(--navy)]">
          This route is outside the coverage area.
        </h1>
        <p className="mt-3 leading-7 text-muted-foreground">
          Return to the Service Portal demo and choose another destination.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex min-h-11 items-center justify-center rounded-[10px] bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[#226a46] focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[var(--cyan)]"
        >
          Return to Service Portal
        </Link>
      </div>
    </main>
  ),
  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <ClientReadyMarker />
        <TooltipProvider>{children}</TooltipProvider>
        {import.meta.env.DEV ? (
          <TanStackDevtools
            config={{
              position: "bottom-right",
            }}
            plugins={[
              {
                name: "TanStack Router",
                render: <TanStackRouterDevtoolsPanel />,
              },
            ]}
          />
        ) : null}
        <Scripts />
      </body>
    </html>
  )
}

function ClientReadyMarker() {
  useEffect(() => {
    document.documentElement.dataset.servicePortalReady = "true"
    return () => {
      delete document.documentElement.dataset.servicePortalReady
    }
  }, [])

  return null
}
